(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-kci-pack]';

  class KciPackSolutions {
    constructor(root) {
      this.root = root;
      this.revealObserver = null;
      this.sectionObserver = null;
      this.abortController = new AbortController();
    }

    init() {
      if (!this.root || this.root.dataset.kciPackReady === 'true') return;

      this.root.dataset.kciPackReady = 'true';
      this.root.classList.add('kci-pack--enhanced');

      this.setupReveal();
      this.setupNavigation();
    }

    setupReveal() {
      const items = [...this.root.querySelectorAll('[data-kci-reveal]')];
      if (!items.length) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reducedMotion || !('IntersectionObserver' in window)) {
        items.forEach((item) => item.classList.add('is-visible'));
        return;
      }

      this.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.08, rootMargin: '0px 0px -5% 0px' }
      );

      items.forEach((item) => this.revealObserver.observe(item));
    }

    setupNavigation() {
      const nav = this.root.querySelector('[data-kci-pack-nav]');
      if (!nav) return;

      const links = [...nav.querySelectorAll('[data-kci-pack-nav-link]')];
      if (!links.length) return;

      nav.addEventListener(
        'click',
        (event) => {
          const link = event.target.closest('[data-kci-pack-nav-link]');
          if (!link) return;

          const selector = link.getAttribute('href');
          const target = selector ? this.root.querySelector(selector) : null;
          if (!target) return;

          event.preventDefault();

          const navHeight = nav.getBoundingClientRect().height;
          const targetTop = target.getBoundingClientRect().top + window.scrollY - navHeight - 12;

          window.scrollTo({
            top: Math.max(0, targetTop),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });

          links.forEach((item) => item.classList.toggle('is-active', item === link));
          link.setAttribute('aria-current', 'location');

          links.filter((item) => item !== link).forEach((item) => item.removeAttribute('aria-current'));

          if (history.replaceState) history.replaceState(null, '', selector);
        },
        { signal: this.abortController.signal }
      );

      if (!('IntersectionObserver' in window)) return;

      const sections = links
        .map((link) => {
          const selector = link.getAttribute('href');
          const section = selector ? this.root.querySelector(selector) : null;
          return section ? { link, section } : null;
        })
        .filter(Boolean);

      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

          if (!visible) return;

          sections.forEach(({ link, section }) => {
            const active = section === visible.target;
            link.classList.toggle('is-active', active);
            if (active) link.setAttribute('aria-current', 'location');
            else link.removeAttribute('aria-current');
          });
        },
        { rootMargin: '-18% 0px -65% 0px', threshold: [0, 0.1, 0.25] }
      );

      sections.forEach(({ section }) => this.sectionObserver.observe(section));
    }

    destroy() {
      this.revealObserver?.disconnect();
      this.sectionObserver?.disconnect();
      this.abortController.abort();
      this.root.classList.remove('kci-pack--enhanced');
      delete this.root.dataset.kciPackReady;
    }
  }

  const instances = new WeakMap();

  const mount = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (instances.has(root)) return;
      const instance = new KciPackSolutions(root);
      instance.init();
      instances.set(root, instance);
    });
  };

  const unmount = (scope) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
})();
